import React from 'react';
import { Filter, X } from 'lucide-react';
import type { SlicerOption, SlicerValues } from './slicerUtils.ts';

/**
 * One slicer's definition: a select-driven client-side filter over the
 * page's primary dataset. `options` is derived from the loaded rows (see
 * `deriveSlicerOptions` in `slicerUtils.ts`) — the backend analytics
 * endpoints are org-scoped only, no filter query params, so every slicer
 * here filters already-fetched rows in the browser.
 */
export type SlicerDef = {
  key: string;
  label: string;
  options: SlicerOption[];
};

/**
 * Shared compact filter bar used at the top of every Feature 5.3 persona
 * dashboard (below the `PageHeader`, above the KPI row). Renders one native
 * `<select>` per slicer plus an active-filter count chip and a Clear button.
 * Generic over any `SlicerDef[]` — each persona dashboard supplies its own
 * slicer definitions (Cycle/Process/Status-equivalent fields available on
 * that persona's primary dataset).
 */
export const AnalyticsSlicerBar: React.FC<{
  slicers: SlicerDef[];
  values: SlicerValues;
  onChange: (key: string, value: string) => void;
  onClear: () => void;
  activeCount: number;
}> = ({ slicers, values, onChange, onClear, activeCount }) => {
  if (slicers.length === 0) return null;
  return (
    <div className="mb-6 flex flex-wrap items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <span className="mr-1 inline-flex shrink-0 items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
        <Filter className="h-3.5 w-3.5" aria-hidden />
        Filters
      </span>
      {slicers.map((slicer) => {
        const active = !!values[slicer.key];
        return (
          <select
            key={slicer.key}
            aria-label={slicer.label}
            value={values[slicer.key] ?? ''}
            onChange={(e) => onChange(slicer.key, e.target.value)}
            className={`rounded-lg border px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 ${
              active
                ? 'border-blue-300 bg-blue-50 font-medium text-blue-700'
                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
            }`}
          >
            <option value="">{slicer.label}: All</option>
            {slicer.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      })}
      <div className="ml-auto flex shrink-0 items-center gap-2">
        {activeCount > 0 && (
          <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">
            {activeCount} active
          </span>
        )}
        <button
          type="button"
          onClick={onClear}
          disabled={activeCount === 0}
          className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
          Clear
        </button>
      </div>
    </div>
  );
};

export default AnalyticsSlicerBar;
